import { OnboardingService } from "@/shared/api/services";

/** Numeric user id for GET /customers/{userId} — must be a positive integer (never NaN). */
function parseOnboardingUserId(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n =
    typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(n) || n <= 0 || Math.floor(n) !== n) return null;
  return n;
}

/**
 * Values safe to interpolate into customer API paths (never NaN).
 * Numeric strings normalize to number; opaque string IDs (e.g. UUID) are kept as-is.
 */
function normalizeCustomerIdCandidate(value: unknown): string | number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const t = value.trim();
    if (!t || t.toLowerCase() === "nan") return null;
    const n = Number(t);
    if (Number.isFinite(n) && String(n) === t) return n;
    if (Number.isFinite(n)) return n;
    return t;
  }
  return null;
}

export async function getCustomerId() {
  let resolvedCustomerId: string | number | null = null;

  // 1) Query param has highest precedence
  const searchParams = new URLSearchParams(window.location.search);
  const qpCustomerId = searchParams.get("customerId");
  if (qpCustomerId) {
    const normalized = normalizeCustomerIdCandidate(qpCustomerId);
    if (normalized !== null) resolvedCustomerId = normalized;
  }

  // 2) Local customer snapshot
  if (!resolvedCustomerId) {
    try {
      const storedCustomerRaw =
        typeof window !== "undefined"
          ? localStorage.getItem("customerData")
          : null;
      const storedCustomer = storedCustomerRaw
        ? JSON.parse(storedCustomerRaw)
        : null;
      const localCustomerId = storedCustomer?.customerId || storedCustomer?.id;
      const normalized = normalizeCustomerIdCandidate(localCustomerId);
      if (normalized !== null) resolvedCustomerId = normalized;
    } catch {}
  }

  // 3) Derive from logged-in user via onboarding service
  if (!resolvedCustomerId) {
    try {
      const storedUserRaw =
        typeof window !== "undefined" ? localStorage.getItem("userData") : null;
      const storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;
      const userId = parseOnboardingUserId(storedUser?.userId);
      if (userId !== null) {
        const customerResp = await OnboardingService.getCustomerByUserId(
          userId,
          { skipErrorCapture: true }
        );
        const got = customerResp?.data;
        const derivedId = got?.customerId || got?.id;
        const normalized = normalizeCustomerIdCandidate(derivedId);
        if (normalized !== null) {
          resolvedCustomerId = normalized;
          try {
            localStorage.setItem("customerData", JSON.stringify(got));
          } catch {}
        }
      }
    } catch {}
  }

  return resolvedCustomerId;
}
